import { useEffect, useState } from 'react';
import { memosAPI, teamAPI, tasksAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import {
  Plus, MessageSquare, Clock, User, AlertCircle, CheckCircle, MoreHorizontal,
  Search, Filter, Send, Tag, Calendar, ArrowRight, ChevronDown, CheckSquare,
  FileText, Globe, Lock, ListTodo
} from 'lucide-react';
import { AssigneeSelect } from '../components/AssigneeSelect';

export default function MemosPage() {
  const { user } = useAuth();
  const [memos, setMemos] = useState([]);
  const [teamUsers, setTeamUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMemo, setSelectedMemo] = useState(null);
  const [memoTasks, setMemoTasks] = useState([]);
  const [createDialog, setCreateDialog] = useState(false);
  const [detailDialog, setDetailDialog] = useState(false);
  const [taskDialog, setTaskDialog] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all'); // internal or external
  const [searchQuery, setSearchQuery] = useState('');
  
  const [memoForm, setMemoForm] = useState({
    title: '',
    content: '',
    memo_type: 'internal',
    priority: 'normal',
    assigned_to: '',
    due_date: '',
    tags: []
  });
  
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    priority: 'medium',
    due_date: '',
    assigned_to: ''
  });
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const fetchData = async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      
      const [memosRes, teamRes] = await Promise.all([
        memosAPI.getAll(params),
        teamAPI.getUsers()
      ]);
      setMemos(memosRes.data);
      setTeamUsers(teamRes.data);
    } catch (error) {
      toast.error('Failed to fetch memos');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMemo = async (e) => {
    e.preventDefault();
    if (!memoForm.title || !memoForm.content) {
      toast.error('Title and content are required');
      return;
    }
    try {
      await memosAPI.create({
        ...memoForm,
        due_date: memoForm.due_date ? new Date(memoForm.due_date).toISOString() : null
      });
      toast.success('Memo created');
      setCreateDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to create memo');
    }
  };

  const handleUpdateStatus = async (memoId, newStatus) => {
    try {
      await memosAPI.update(memoId, { status: newStatus });
      toast.success('Status updated');
      fetchData();
      if (selectedMemo?.id === memoId) {
        const updated = await memosAPI.getById(memoId);
        setSelectedMemo(updated.data);
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedMemo) return;
    try {
      await memosAPI.addComment(selectedMemo.id, { content: newComment });
      toast.success('Comment added');
      setNewComment('');
      const updated = await memosAPI.getById(selectedMemo.id);
      setSelectedMemo(updated.data);
      fetchData();
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const handleDeleteMemo = async (memoId) => {
    if (!confirm('Are you sure you want to delete this memo?')) return;
    try {
      await memosAPI.delete(memoId);
      toast.success('Memo deleted');
      setDetailDialog(false);
      setSelectedMemo(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete memo');
    }
  };

  const openMemoDetail = async (memo) => {
    try {
      const [memoRes, tasksRes] = await Promise.all([
        memosAPI.getById(memo.id),
        memosAPI.getTasks(memo.id).catch(() => ({ data: [] }))
      ]);
      setSelectedMemo(memoRes.data);
      setMemoTasks(tasksRes.data || []);
      setDetailDialog(true);
    } catch (error) {
      toast.error('Failed to load memo details');
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    if (!selectedMemo) return;
    
    try {
      await memosAPI.createTask(selectedMemo.id, {
        ...taskForm,
        due_date: taskForm.due_date ? new Date(taskForm.due_date).toISOString() : null
      });
      toast.success('Task created and linked to memo');
      setTaskDialog(false);
      setTaskForm({ title: '', description: '', priority: 'medium', due_date: '', assigned_to: '' });
      // Refresh tasks
      const tasksRes = await memosAPI.getTasks(selectedMemo.id);
      setMemoTasks(tasksRes.data || []);
    } catch (error) {
      toast.error('Failed to create task');
    }
  };

  const addTag = () => {
    if (tagInput.trim() && !memoForm.tags.includes(tagInput.trim())) {
      setMemoForm(prev => ({ ...prev, tags: [...prev.tags, tagInput.trim()] }));
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setMemoForm(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }));
  };

  const resetForm = () => {
    setMemoForm({ title: '', content: '', memo_type: 'internal', priority: 'normal', assigned_to: '', due_date: '', tags: [] });
  };

  const filteredMemos = memos.filter(memo => {
    const matchesSearch = !searchQuery || 
      memo.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
      memo.content.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = typeFilter === 'all' || memo.memo_type === typeFilter;
    return matchesSearch && matchesType;
  });

  const priorityColors = {
    low: 'bg-slate-100 text-slate-700',
    normal: 'bg-blue-100 text-blue-700',
    high: 'bg-amber-100 text-amber-700',
    urgent: 'bg-red-100 text-red-700'
  };

  const statusColors = {
    open: 'bg-blue-100 text-blue-700',
    in_progress: 'bg-amber-100 text-amber-700',
    resolved: 'bg-emerald-100 text-emerald-700',
    closed: 'bg-slate-100 text-slate-600'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="memos-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Internal Memos & Notes</h1>
          <p className="text-muted-foreground">Team collaboration, notes, and task management</p>
        </div>
        <Button onClick={() => setCreateDialog(true)} data-testid="create-memo-btn">
          <Plus className="h-4 w-4 mr-2" /> New Memo/Note
        </Button>
      </div>

      {/* Type Tabs & Filters */}
      <div className="space-y-4 mb-6">
        <Tabs value={typeFilter} onValueChange={setTypeFilter} className="w-full">
          <TabsList>
            <TabsTrigger value="all" className="gap-2">
              <FileText className="h-4 w-4" />
              All
            </TabsTrigger>
            <TabsTrigger value="internal" className="gap-2">
              <Lock className="h-4 w-4" />
              Internal Notes
            </TabsTrigger>
            <TabsTrigger value="external" className="gap-2">
              <Globe className="h-4 w-4" />
              External Notes
            </TabsTrigger>
          </TabsList>
        </Tabs>
        
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search memos..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="memo-search"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]" data-testid="status-filter">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Kanban-style columns */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {['open', 'in_progress', 'resolved', 'closed'].map(status => (
          <div key={status} className="bg-slate-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold capitalize text-sm flex items-center gap-2">
                {status === 'open' && <AlertCircle className="h-4 w-4 text-blue-600" />}
                {status === 'in_progress' && <Clock className="h-4 w-4 text-amber-600" />}
                {status === 'resolved' && <CheckCircle className="h-4 w-4 text-emerald-600" />}
                {status === 'closed' && <CheckCircle className="h-4 w-4 text-slate-400" />}
                {status.replace('_', ' ')}
              </h3>
              <Badge variant="secondary" className="text-xs">
                {filteredMemos.filter(m => m.status === status).length}
              </Badge>
            </div>
            <ScrollArea className="h-[500px]">
              <div className="space-y-3 pr-2">
                {filteredMemos.filter(m => m.status === status).map(memo => (
                  <Card 
                    key={memo.id} 
                    className="cursor-pointer hover:shadow-md transition-shadow border-l-4"
                    style={{ borderLeftColor: memo.priority === 'urgent' ? '#ef4444' : memo.priority === 'high' ? '#f59e0b' : '#3b82f6' }}
                    onClick={() => openMemoDetail(memo)}
                    data-testid={`memo-card-${memo.id}`}
                  >
                    <CardContent className="p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <p className="font-medium text-sm line-clamp-1 flex-1">{memo.title}</p>
                        {memo.memo_type === 'external' ? (
                          <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                            <Globe className="h-3 w-3 mr-1" />
                            External
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs bg-slate-50 text-slate-600 border-slate-200">
                            <Lock className="h-3 w-3 mr-1" />
                            Internal
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-2 mb-3">{memo.content}</p>
                      <div className="flex items-center justify-between">
                        <Badge className={`text-xs ${priorityColors[memo.priority]}`}>
                          {memo.priority}
                        </Badge>
                        {memo.assignee && (
                          <div className="flex items-center gap-1">
                            <Avatar className="h-5 w-5">
                              <AvatarFallback className="text-xs bg-slate-200">
                                {memo.assignee.full_name?.[0] || memo.assignee.email?.[0] || '?'}
                              </AvatarFallback>
                            </Avatar>
                          </div>
                        )}
                      </div>
                      {memo.due_date && (
                        <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
                          <Calendar className="h-3 w-3" />
                          {format(new Date(memo.due_date), 'MMM d')}
                        </div>
                      )}
                      {memo.tags?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {memo.tags.slice(0, 2).map(tag => (
                            <Badge key={tag} variant="outline" className="text-xs px-1">
                              {tag}
                            </Badge>
                          ))}
                          {memo.tags.length > 2 && (
                            <Badge variant="outline" className="text-xs px-1">+{memo.tags.length - 2}</Badge>
                          )}
                        </div>
                      )}
                      <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                        {memo.comments?.length > 0 && (
                          <span className="flex items-center gap-1">
                            <MessageSquare className="h-3 w-3" />
                            {memo.comments.length}
                          </span>
                        )}
                        {memo.linked_tasks?.length > 0 && (
                          <span className="flex items-center gap-1 text-blue-600">
                            <CheckSquare className="h-3 w-3" />
                            {memo.linked_tasks.length} task{memo.linked_tasks.length !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {filteredMemos.filter(m => m.status === status).length === 0 && (
                  <p className="text-center text-sm text-muted-foreground py-8">No memos</p>
                )}
              </div>
            </ScrollArea>
          </div>
        ))}
      </div>

      {/* Create Memo Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Memo/Note</DialogTitle>
            <DialogDescription>Create a new internal or external memo for team collaboration</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateMemo} className="space-y-4">
            <div className="space-y-2">
              <Label>Note Type</Label>
              <Select value={memoForm.memo_type} onValueChange={(v) => setMemoForm(p => ({ ...p, memo_type: v }))}>
                <SelectTrigger data-testid="memo-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="internal">
                    <span className="flex items-center gap-2">
                      <Lock className="h-4 w-4" />
                      Internal (Team Only)
                    </span>
                  </SelectItem>
                  <SelectItem value="external">
                    <span className="flex items-center gap-2">
                      <Globe className="h-4 w-4" />
                      External (Client-facing)
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {memoForm.memo_type === 'internal' 
                  ? 'Internal notes are only visible to team members' 
                  : 'External notes may be shared with clients'}
              </p>
            </div>
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={memoForm.title}
                onChange={(e) => setMemoForm(p => ({ ...p, title: e.target.value }))}
                placeholder="Brief title for the memo"
                required
                data-testid="memo-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Content</Label>
              <Textarea
                value={memoForm.content}
                onChange={(e) => setMemoForm(p => ({ ...p, content: e.target.value }))}
                placeholder="Detailed content..."
                rows={4}
                required
                data-testid="memo-content-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select value={memoForm.priority} onValueChange={(v) => setMemoForm(p => ({ ...p, priority: v }))}>
                  <SelectTrigger data-testid="memo-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Assign To</Label>
                <AssigneeSelect
                  value={memoForm.assigned_to}
                  onValueChange={(v) => setMemoForm(p => ({ ...p, assigned_to: v === 'unassigned' ? '' : v }))}
                  placeholder="Select assignee"
                  includeAI={true}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Due Date (optional)</Label>
              <Input
                type="datetime-local"
                value={memoForm.due_date}
                onChange={(e) => setMemoForm(p => ({ ...p, due_date: e.target.value }))}
                data-testid="memo-due-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Tags</Label>
              <div className="flex gap-2">
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  placeholder="Add a tag"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                />
                <Button type="button" variant="outline" onClick={addTag}>Add</Button>
              </div>
              {memoForm.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {memoForm.tags.map(tag => (
                    <Badge key={tag} variant="secondary" className="cursor-pointer" onClick={() => removeTag(tag)}>
                      {tag} ×
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateDialog(false)}>Cancel</Button>
              <Button type="submit" data-testid="memo-submit-btn">Create Memo</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Memo Detail Dialog */}
      <Dialog open={detailDialog} onOpenChange={setDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          {selectedMemo && (
            <>
              <DialogHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <DialogTitle className="text-xl">{selectedMemo.title}</DialogTitle>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge className={priorityColors[selectedMemo.priority]}>
                        {selectedMemo.priority}
                      </Badge>
                      <Badge className={statusColors[selectedMemo.status]}>
                        {selectedMemo.status.replace('_', ' ')}
                      </Badge>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleUpdateStatus(selectedMemo.id, 'open')}>
                        Mark as Open
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleUpdateStatus(selectedMemo.id, 'in_progress')}>
                        Mark as In Progress
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleUpdateStatus(selectedMemo.id, 'resolved')}>
                        Mark as Resolved
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleUpdateStatus(selectedMemo.id, 'closed')}>
                        Mark as Closed
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        className="text-red-600" 
                        onClick={() => handleDeleteMemo(selectedMemo.id)}
                      >
                        Delete Memo
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </DialogHeader>
              
              <div className="flex-1 overflow-y-auto space-y-4 py-4">
                {/* Meta info */}
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <User className="h-4 w-4" />
                    Created by {selectedMemo.creator?.full_name || selectedMemo.creator?.email || 'Unknown'}
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    {formatDistanceToNow(new Date(selectedMemo.created_at), { addSuffix: true })}
                  </div>
                </div>

                {selectedMemo.assignee && (
                  <div className="flex items-center gap-2 text-sm">
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    <span>Assigned to:</span>
                    <Avatar className="h-5 w-5">
                      <AvatarFallback className="text-xs">
                        {selectedMemo.assignee.full_name?.[0] || '?'}
                      </AvatarFallback>
                    </Avatar>
                    <span className="font-medium">{selectedMemo.assignee.full_name || selectedMemo.assignee.email}</span>
                  </div>
                )}

                {selectedMemo.due_date && (
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span>Due:</span>
                    <span className="font-medium">{format(new Date(selectedMemo.due_date), 'PPp')}</span>
                  </div>
                )}

                {selectedMemo.tags?.length > 0 && (
                  <div className="flex items-center gap-2">
                    <Tag className="h-4 w-4 text-muted-foreground" />
                    {selectedMemo.tags.map(tag => (
                      <Badge key={tag} variant="outline">{tag}</Badge>
                    ))}
                  </div>
                )}

                <Separator />

                {/* Content */}
                <div className="prose prose-sm max-w-none">
                  <p className="whitespace-pre-wrap">{selectedMemo.content}</p>
                </div>

                <Separator />

                {/* Linked Tasks Section */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold flex items-center gap-2">
                      <CheckSquare className="h-4 w-4" />
                      Linked Tasks ({memoTasks.length})
                    </h4>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setTaskDialog(true)}
                      data-testid="memo-create-task-btn"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Task
                    </Button>
                  </div>
                  {memoTasks.length > 0 ? (
                    <div className="space-y-2">
                      {memoTasks.map(task => (
                        <div 
                          key={task.id} 
                          className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg"
                        >
                          <CheckSquare className={`h-4 w-4 ${task.status === 'completed' ? 'text-green-600' : 'text-slate-400'}`} />
                          <div className="flex-1 min-w-0">
                            <p className={`font-medium text-sm ${task.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>
                              {task.title}
                            </p>
                            <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                              <Badge variant="outline" className="text-xs">
                                {task.priority}
                              </Badge>
                              <Badge variant="outline" className="text-xs">
                                {task.status}
                              </Badge>
                              {task.due_date && (
                                <span className="flex items-center gap-1">
                                  <Calendar className="h-3 w-3" />
                                  {format(new Date(task.due_date), 'MMM d')}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-sm text-muted-foreground py-4 bg-slate-50 rounded-lg">
                      <ListTodo className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                      No tasks linked to this memo
                    </p>
                  )}
                </div>

                <Separator />

                {/* Comments */}
                <div>
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" />
                    Comments ({selectedMemo.comments?.length || 0})
                  </h4>
                  <ScrollArea className="h-[200px]">
                    <div className="space-y-3 pr-2">
                      {selectedMemo.comments?.map((comment, idx) => (
                        <div key={comment.id || idx} className="flex gap-3 p-3 bg-slate-50 rounded-lg">
                          <Avatar className="h-8 w-8">
                            <AvatarFallback className="text-xs bg-slate-200">
                              {comment.author?.full_name?.[0] || '?'}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-sm">
                                {comment.author?.full_name || comment.author?.email || 'Unknown'}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                              </span>
                            </div>
                            <p className="text-sm">{comment.content}</p>
                          </div>
                        </div>
                      ))}
                      {(!selectedMemo.comments || selectedMemo.comments.length === 0) && (
                        <p className="text-center text-sm text-muted-foreground py-4">No comments yet</p>
                      )}
                    </div>
                  </ScrollArea>
                </div>
              </div>

              {/* Add comment */}
              <div className="flex gap-2 pt-4 border-t">
                <Input
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Add a comment..."
                  onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
                  data-testid="memo-comment-input"
                />
                <Button onClick={handleAddComment} disabled={!newComment.trim()} data-testid="memo-comment-submit">
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Task Dialog */}
      <Dialog open={taskDialog} onOpenChange={setTaskDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Task from Memo</DialogTitle>
            <DialogDescription>
              Create a task linked to: {selectedMemo?.title}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateTask} className="space-y-4">
            <div className="space-y-2">
              <Label>Task Title</Label>
              <Input
                value={taskForm.title}
                onChange={(e) => setTaskForm(p => ({ ...p, title: e.target.value }))}
                placeholder="What needs to be done?"
                required
                data-testid="task-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={taskForm.description}
                onChange={(e) => setTaskForm(p => ({ ...p, description: e.target.value }))}
                placeholder="Task details..."
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select value={taskForm.priority} onValueChange={(v) => setTaskForm(p => ({ ...p, priority: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Due Date</Label>
                <Input
                  type="datetime-local"
                  value={taskForm.due_date}
                  onChange={(e) => setTaskForm(p => ({ ...p, due_date: e.target.value }))}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Assign To</Label>
              <AssigneeSelect
                value={taskForm.assigned_to}
                onValueChange={(v) => setTaskForm(p => ({ ...p, assigned_to: v === 'unassigned' ? '' : v }))}
                placeholder="Select team member or AI agent"
                includeAI={true}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setTaskDialog(false)}>Cancel</Button>
              <Button type="submit" data-testid="task-submit-btn">Create Task</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
